import { Link } from "wouter";
import type { PublicProperty } from "@shared/schema";
import { KUALOA_PROPERTY_SLUG } from "@shared/property-context";
import { useFeatureFlags } from "@/hooks/use-feature-flags";
import { ClickableCard } from "./clickable-card";
import { RateLine } from "./rate-line";
import { TrustLine } from "./trust-line";
import { NextOpenWindows } from "./next-open-windows";
import { NewListingBadge } from "./new-listing-badge";
import { LastMinuteOffer } from "@/components/promotions/last-minute-offer";
const img = (src: string, w: number) => `/img?src=${encodeURIComponent(src)}&w=${w}&q=90`;
function coverOf(home: PublicProperty) { return home.hero_image_url || [...(home.photos || [])].sort((a,b)=>a.sort_order-b.sort_order)[0]?.url; }
function cardBlurb(home: { short_description?: string | null; description?: string | null }): string {
  const short = home.short_description?.trim();
  if (short) return short;
  const text = (home.description || "").replace(/\s+/g, " ").trim();
  if (!text) return "";
  const match = text.match(/^[^.!?]{40,180}[.!?]/);
  return match ? match[0] : text.slice(0, 160).replace(/\s\S*$/, "") + "…";
}

export function PropertyListings({properties}: {properties: PublicProperty[]}) {
const flags=useFeatureFlags();
return (
          <ul className="flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar -mx-4 px-4 pb-2 md:mx-0 md:px-0 md:pb-0 md:grid md:grid-cols-2 md:gap-8 md:overflow-visible lg:gap-10">
            {properties.map((home) => {
              const cover = coverOf(home);
              const href = home.slug === KUALOA_PROPERTY_SLUG && !flags.propertyRoutes ? "/stay" : `/properties/${home.slug}`;
              return (
                <ClickableCard key={home.id} href={href} className="group flex flex-col snap-start shrink-0 w-[86%] md:w-auto border border-sand-deep bg-sand overflow-hidden transition-shadow hover:shadow-lg" data-testid={`home-card-${home.slug}`}>
                  <Link href={href} className="block">
                    <div className="relative aspect-[3/2] bg-sand overflow-hidden">
                      <NewListingBadge slug={home.slug} variant="overlay" />
                      {cover && (
                        <img
                          src={img(cover, 960)}
                          srcSet={`${img(cover, 640)} 640w, ${img(cover, 960)} 960w, ${img(cover, 1200)} 1200w, ${img(cover, 1920)} 1920w`}
                          sizes="(min-width: 768px) 50vw, 100vw"
                          alt={`${home.title}, ${home.public_location_label || home.general_location}`}
                          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                          loading="lazy"
                          decoding="async"
                        />
                      )}
                    </div>
                  </Link>
                  {/* The body is a column and the price row sits at its foot, so the
                      buttons on every card line up whatever the description length;
                      the description itself is capped at three lines. */}
                  <div className="flex flex-1 flex-col text-center p-6 md:p-7">
                    <p className="font-sans text-[11px] uppercase tracking-[0.2em] text-driftwood">{home.public_location_label || home.general_location}</p>
                    <h2 className="mt-2 font-serif text-2xl md:text-3xl text-ink">
                      <Link href={href} className="font-serif font-[500] hover:text-ink/70 transition-colors">{home.title}</Link>
                    </h2>
                    <p className="mt-2 font-sans text-luxury-sm text-ink/70">
                      {home.bedrooms} bedrooms · {home.bathrooms} bathrooms · up to {home.max_guests} guests
                    </p>
                    {cardBlurb(home) && (
                      <p className="mt-4 line-clamp-3 font-sans text-luxury-base font-light leading-relaxed text-ink-soft">{cardBlurb(home)}</p>
                    )}
                    <TrustLine home={home} className="mt-3 justify-center" />
                    <LastMinuteOffer slug={home.slug} variant="badge" className="mt-3 self-center" />
                    <NextOpenWindows slug={home.slug} className="mt-2" />
                    <div className="mt-auto flex flex-col items-center justify-center gap-4 pt-6">
                      <RateLine home={home} testId={`home-card-rate-${home.slug}`} />
                      <div className="flex flex-wrap justify-center gap-2">
                        <Link href={href} aria-label={`View Property: ${home.title}`} className="inline-flex min-h-11 items-center rounded border border-ink bg-ink px-4 font-sans text-sm text-shell hover:bg-ink/90" data-testid={`home-card-cta-${home.slug}`}>
                          View Property
                        </Link>
                        <Link href={`/properties/${home.slug}/book`} aria-label={`Book Now: ${home.title}`} className="inline-flex min-h-11 items-center rounded border border-ink px-4 font-sans text-sm text-ink hover:bg-shell" data-testid={`home-card-book-${home.slug}`}>
                          Book Now
                        </Link>
                      </div>
                    </div>
                  </div>
                </ClickableCard>
              );
            })}
          </ul>
);
}
